import { Camera, Mesh, MeshBuilder, TransformNode, UniversalCamera, Vector3, Matrix, type Scene } from "@babylonjs/core";
import {
  abilIdx,
  BARRIERS,
  cameraPos,
  cameraScale,
  CartState,
  CONSTRUCTS,
  constructMaterialize,
  EntityType,
  eyeHeight,
  heldWeapon,
  heroDef,
  previewTargetPoint,
  targetingSlot,
  targetSpec,
  heroCapsules,
  HERO_LIST,
  HERO_VARS,
  type HeroId,
  hv,
  initPhysics,
  GameMode,
  loadMap,
  type MapData,
  type MapId,
  MAPS,
  mapUrl,
  MATCH_NET_ID,
  MODES,
  pointAt,
  NetClient,
  otherTeam,
  Phase,
  PROTOCOL_VERSION,
  type SimEvent,
  Slot,
  STATUS_BITS,
  statusBits,
  Team,
  TICK_RATE,
  trackPose,
  type Vec3,
  viewDir,
  withConditions,
} from "@rivalwatch/shared";
import { connect } from "../net/connect.ts";
import { type AbilityHud, type ConvoyHud, type MatchHud, pushChat, toast, ui } from "../ui/store.ts";
import { chat as chatCue } from "../audio/cues/ui.ts";
import { audio } from "../audio/engine.ts";
import { type ActorAudio, AudioDirector, type ConstructAudio, type MatchAudio, type ProjectileAudio, type Rel, type SelfAudio, type ZoneAudio } from "../audio/director.ts";
import { CLIENT_HEROES } from "../heroes/index.ts";
import type { ConstructFrame, ConstructView, Rel as ViewRel, WorldRef } from "../heroes/types.ts";
import { animateAvatar, type Avatar, buildAvatar } from "./avatars.ts";
import { Fx, type ProjectileSync } from "./fx.ts";
import { TargetPreview } from "./targeting.ts";
import { Input } from "./input.ts";
import { glow, toon } from "./materials.ts";
import { flatbedPayload, NEUTRAL, sleeperTrack } from "../maps/default.ts";
import type { PayloadView } from "../maps/types.ts";
import { loadHeroModels } from "./models.ts";
import { PhotoRig } from "./photo.ts";
import { renderHeroPortraits } from "./portraits.ts";
import { StatusFx } from "./statusfx.ts";
import { createWorld, type World } from "./world.ts";

export const CLIENT_BUILD = (import.meta.env.VITE_BUILD_SHA as string | undefined) ?? "dev";
const FRIEND = "#2FB4FF";
const FOE = "#FF3355";
const GOLD = "#E8B84A";

interface RemoteView {
  avatar: Avatar;
  form: number;
  last: Vec3;
  speed: number;
  tag: HTMLDivElement;
  /** Replicated hero vars v0..v3, reused per frame for the avatar hook. */
  vars: number[];
}

type ConstructRenderFrame = ConstructFrame & { build?: (scene: Scene, frame: ConstructFrame) => ConstructView };

/** The in-match client: rendering, prediction, interpolation, effects, HUD feed. */
export class Game {
  readonly input: Input;
  readonly photo = new PhotoRig();
  private director?: AudioDirector;
  world!: World;
  private camera!: UniversalCamera;
  net!: NetClient;
  private map!: MapData;
  private fx!: Fx;
  private targetPreview!: TargetPreview;
  private remotes = new Map<number, RemoteView>();
  private local?: Avatar;
  private localHero = -1;
  private localForm = -1;
  private readonly localVars = [0, 0, 0, 0];
  private myTeam: Team = Team.A;
  private renderPrev: Vec3 = { x: 0, y: 0, z: 0 };
  private renderCurr: Vec3 = { x: 0, y: 0, z: 0 };
  private visualErr: Vec3 = { x: 0, y: 0, z: 0 };
  private hudTimer = 0;
  private mapId: MapId = "foundry";
  private wantMap?: MapId;
  private mapTask: Promise<void> = Promise.resolve();
  /** Everything buildObjective() made, for the next map swap. */
  private objective: { dispose(): void }[] = [];
  private pointRing?: Mesh;
  private pointDisc?: Mesh;
  /** Control: the stage (sub-map) in play, as the last snapshot said. */
  private stage = 0;
  private packs: Mesh[] = [];
  private packBases: Mesh[] = [];
  private cart?: PayloadView;
  private checkpointBands: Mesh[] = [];
  private gates: Mesh[] = [];
  private lastFrame = performance.now();
  private fpsFrames = 0;
  private fpsTime = 0;
  private shake = 0;
  private disposed = false;
  private killedBy?: { name: string; hero: HeroId; with: { name: string; hero: HeroId }[] };
  private lastButtons = 0;
  private floaters = new Map<string, { el: HTMLDivElement; amount: number; at: number; timer: number }>();
  private hitboxMeshes = new Map<number, Mesh[]>();
  private readonly statusFx = new WeakMap<Avatar, StatusFx>();
  private stealthRevealUntil = new Map<number, number>();
  private localCameraVis = 1;

  private constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly tagsEl: HTMLElement,
  ) {
    this.input = new Input(canvas);
  }

  static async start(canvas: HTMLCanvasElement, tagsEl: HTMLElement, room: string, name: string, token: string): Promise<Game> {
    const g = new Game(canvas, tagsEl);
    await g.init(room, name, token);
    return g;
  }

  private async init(room: string, name: string, token: string) {
    const params = new URLSearchParams(location.search);
    ui.status.value = "connecting";
    // Load the room's map up front (a room that doesn't exist yet starts on Control's map).
    const info = (await fetch(`/api/rooms/${encodeURIComponent(room)}`)
      .then((r) => (r.ok ? r.json() : undefined))
      .catch(() => undefined)) as { map?: MapId } | undefined;
    this.mapId = info?.map && info.map in MAPS ? info.map : MODES[GameMode.Control].map;
    ui.statusText.value = `Loading ${MAPS[this.mapId].title}…`;
    const [, glb] = await Promise.all([initPhysics(), fetch(mapUrl(this.mapId)).then((r) => r.arrayBuffer())]);
    this.map = loadMap(glb);
    this.world = await createWorld(this.canvas, this.mapId);
    ui.statusText.value = "Suiting up…";
    await loadHeroModels(this.world.scene);
    const scene = this.world.scene;
    this.camera = new UniversalCamera("cam", new Vector3(0, 10, 30), scene);
    this.camera.fov = 1.15;
    this.camera.minZ = 0.1;
    this.camera.maxZ = 400;
    this.camera.inputs.clear();
    this.world.attachCamera(this.camera);
    this.fx = new Fx(scene);
    this.targetPreview = new TargetPreview(scene);
    this.buildObjective();

    ui.statusText.value = "Connecting…";
    const { transport, kind } = await connect(room, params.get("transport"));
    ui.transport.value = kind;
    const lag = Number(params.get("lag") ?? 0);
    const loss = Number(params.get("loss") ?? 0);
    const t = lag || loss ? withConditions(transport, { latencyMs: lag / 2, jitterMs: Number(params.get("jitter") ?? 0), lossPct: loss }) : transport;
    this.net = new NetClient(this.map, t);
    const net = this.net;
    this.director = new AudioDirector({
      get me() {
        return net.localNetId;
      },
      relOf: (id) => this.relOfNet(id),
      posOf: (id) => {
        const r = net.latest()?.get(id);
        return r && r.x !== undefined ? { x: r.x, y: r.y!, z: r.z! } : undefined;
      },
      los: (a, b) => net.predict.physics.los(a, b),
    });
    this.wireNet();
    this.net.hello(name, token, CLIENT_BUILD);
    t.onClose = () => {
      if (this.disposed) return;
      // Keep a more specific error (an incompatible server) on screen.
      if (ui.status.value !== "error") {
        ui.status.value = "disconnected";
        ui.statusText.value = "Connection lost";
      }
      // The last snapshot would otherwise hold every loop (jets, capture alarm, heartbeat) forever.
      this.director?.dispose();
      this.director = undefined;
    };
    this.world.engine.runRenderLoop(() => this.frame());
    // Portrait capture resolves on engine frame boundaries, so it runs once the loop is live.
    if (!Object.keys(ui.portraits.value).length)
      renderHeroPortraits(this.world.engine).then(
        (p) => (ui.portraits.value = p),
        (e) => console.warn("hero portraits unavailable", e),
      );
  }

  private wireNet() {
    const net = this.net;
    net.onWelcome = (m) => {
      ui.you.value = m.you;
      const outdated = CLIENT_BUILD !== "dev" && m.build !== "dev" && m.build !== CLIENT_BUILD;
      if (outdated) {
        toast("A new version is live — reloading…");
        setTimeout(() => location.reload(), 1200);
      }
      if (!net.compatible) {
        // Its snapshots and lobby can't be read. Say so rather than sit on a half-drawn world
        // (in development: a game server started before a pull and never restarted).
        ui.status.value = "error";
        ui.statusText.value = outdated
          ? "A new version is live — reloading…"
          : `The game server is running a different version of Rivalwatch (protocol ${m.protocol}; this page speaks ${PROTOCOL_VERSION}). Restart the server, then reconnect.`;
        net.transport.close();
        return;
      }
      ui.status.value = "connected";
    };
    net.onLobby = (l) => {
      if (!net.compatible) return;
      ui.lobby.value = l;
      if (l.map !== (this.wantMap ?? this.mapId)) this.requestMap(l.map);
    };
    net.onChat = (from, text) => {
      pushChat(from, text, !from);
      if (from) audio.play({ bus: "ui", gain: 0.9, priority: 4 }, chatCue);
    };
    net.onNotice = (text, error) => toast(text, error);
    net.onMatchEnd = (s) => (ui.matchEnd.value = s);
    net.onEvent = (e) => this.onEvent(e);
    net.onPredictedEvent = (e) => this.onPredicted(e);
    net.onCorrection = (dx, dy, dz) => {
      // Keep the rendered position continuous; bleed the error off over a few frames.
      this.visualErr.x += dx;
      this.visualErr.y += dy;
      this.visualErr.z += dz;
      for (const p of [this.renderPrev, this.renderCurr]) {
        p.x -= dx;
        p.y -= dy;
        p.z -= dz;
      }
      if (Math.hypot(this.visualErr.x, this.visualErr.y, this.visualErr.z) > 3) this.visualErr = { x: 0, y: 0, z: 0 };
    };
  }

  /**
   * The room switched mode: load its map in place, keeping the connection and seat. Switches
   * queue up, and each one loads whatever the room wants by the time it runs.
   */
  private requestMap(id: MapId) {
    if (!(id in MAPS)) return;
    this.wantMap = id;
    this.mapTask = this.mapTask.then(() => this.switchMap()).catch((e) => {
      console.error("map switch failed", e);
      toast("Couldn't load the map — try reconnecting", true);
    });
  }

  private async switchMap() {
    const id = this.wantMap;
    if (!id || id === this.mapId || this.disposed) return;
    toast(`Loading ${MAPS[id].title}…`);
    const glb = await fetch(mapUrl(id)).then((r) => r.arrayBuffer());
    const map = loadMap(glb);
    await this.world.loadMap(id);
    if (this.disposed) return;
    this.map = map;
    this.mapId = id;
    this.stage = 0;
    this.net.setMap(map);
    this.localHero = -1;
    this.localForm = -1;
    for (const r of this.remotes.values()) {
      this.statusFx.get(r.avatar)?.dispose();
      r.avatar.dispose();
      r.tag.remove();
    }
    if (this.local) this.statusFx.get(this.local)?.dispose();
    this.remotes.clear();
    for (const ms of this.hitboxMeshes.values()) ms.forEach((m) => m.dispose());
    this.hitboxMeshes.clear();
    this.buildObjective();
    toast(`${MODES[MAPS[id].mode].name} on ${MAPS[id].title}`);
  }

  private nameOf(netId: number): string {
    return ui.lobby.value?.players.find((p) => p.netId === netId)?.name ?? "?";
  }

  private teamOfNet(netId: number): number {
    return this.net.latest()?.get(netId)?.team ?? 0;
  }

  private relOfNet(netId: number): Rel | "none" {
    if (netId === this.net.localNetId) return "self";
    const team = this.net.latest()?.get(netId)?.team;
    return team === undefined || team === Team.None ? "none" : team === this.myTeam ? "friend" : "foe";
  }

  // ------------------------------------------------------------------ events

  private onEvent(e: SimEvent) {
    const me = this.net.localNetId;
    this.director?.event(e, false);
    switch (e.type) {
      case "tracer": {
        if (e.owner === me) return;
        const ot = this.teamOfNet(e.owner);
        this.fx.tracer(e.from, e.to, e.kind, ot === undefined ? "none" : ot === this.myTeam ? "friend" : "foe");
        break;
      }
      case "impact":
        this.fx.impact(e.pos, e.kind, 0.35);
        break;
      case "explosion":
        this.fx.explosion(e.pos, e.radius, e.kind);
        break;
      case "damage":
        if (e.attacker === me && e.target !== me) {
          ui.hit.value = { at: performance.now(), crit: e.crit, kill: false };
          this.floatNumber(`d${e.target}`, e.pos, e.amount, e.crit ? "crit" : "dmg");
        }
        if (e.target === me && e.attacker !== me) {
          ui.hurt.value = performance.now();
          const src = e.attacker ? this.net.latest()?.get(e.attacker) : undefined;
          if (src) ui.dmgDirs.value = [...ui.dmgDirs.value.filter((d) => performance.now() - d.at < 1200).slice(-5), { id: e.id! + e.tick! * 65536, at: performance.now(), x: src.x!, z: src.z! }];
          this.shake = Math.min(0.25, this.shake + e.amount / 400);
        }
        this.stealthRevealUntil.set(e.target, performance.now() + 300);
        break;
      case "kill": {
        const id = e.id! + e.tick! * 65536;
        const killerName = e.killer ? this.nameOf(e.killer) : MAPS[this.mapId].title;
        const victimName = this.nameOf(e.victim);
        const person = (n: number) => ({ name: this.nameOf(n), hero: (this.net.latest()?.get(n)?.hero ?? HERO_LIST[0]!.id) as HeroId });
        // Rivals credit: the Final Hit, everyone else who shares the KO, and assists (enablers).
        const ko = e.victim !== me && (e.killer === me || e.kos.includes(me));
        const assist = e.victim !== me && !ko && e.assists.includes(me);
        if (ko) {
          ui.hit.value = { at: performance.now(), crit: false, kill: true };
          const v = this.net.latest()?.get(e.victim);
          if (v) this.floatNumber(`ko${e.victim}`, { x: v.x!, y: v.y! + heroDef(v.hero as HeroId, v.form ?? 0).height + 0.55, z: v.z! }, 1, "ko");
        }
        if (ko || assist) {
          const partner = e.killer && e.killer !== me ? { name: killerName, hero: e.killerHero } : undefined;
          ui.kos.value = [...ui.kos.value.slice(-5), { id, kind: e.killer === me ? "final" : ko ? "shared" : "assist", victim: victimName, victimHero: e.victimHero, partner, at: Date.now() }];
        }
        if (e.victim === me) this.killedBy = e.killer && e.killer !== me ? { name: killerName, hero: e.killerHero as HeroId, with: e.kos.map(person) } : undefined;
        ui.feed.value = [
          ...ui.feed.value.slice(-5),
          {
            id,
            killer: e.killer ? killerName : "",
            killerHero: e.killerHero,
            victim: victimName,
            victimHero: e.victimHero,
            killerFriend: this.teamOfNet(e.killer) === this.myTeam,
            victimFriend: this.teamOfNet(e.victim) === this.myTeam,
            helpers: [...e.kos.map((n) => ({ ...person(n), kind: "ko" as const })), ...e.assists.map((n) => ({ ...person(n), kind: "assist" as const }))],
            mine: e.killer === me || e.victim === me || e.kos.includes(me) || e.assists.includes(me),
            at: Date.now(),
          },
        ];
        break;
      }
      case "heal": {
        if (e.healer !== me || e.target === me) break;
        const t = this.net.latest()?.get(e.target);
        if (t) this.floatNumber(`h${e.target}`, { x: t.x!, y: t.y! + heroDef(t.hero as HeroId, t.form ?? 0).height + 0.2, z: t.z! }, e.amount, "heal", "+");
        break;
      }
      case "pickup":
        this.fx.pickup(e.pos, e.large);
        break;
      case "revive":
        if (e.target === me) toast("You were revived!");
        break;
      default:
        break;
    }
  }

  private onPredicted(e: SimEvent) {
    if (e.type === "tracer") this.fx.tracer(e.from, e.to, e.kind, "self");
    this.director?.event(e, true);
  }

  private toScreen(p: Vec3): { x: number; y: number } | undefined {
    const eng = this.world.engine;
    const w = eng.getRenderWidth();
    const h = eng.getRenderHeight();
    const sp = Vector3.Project(new Vector3(p.x, p.y, p.z), Matrix.IdentityReadOnly, this.world.scene.getTransformMatrix(), this.camera.viewport.toGlobal(w, h));
    if (sp.z <= 0 || sp.z >= 1) return undefined;
    return { x: (sp.x * this.canvas.clientWidth) / w, y: (sp.y * this.canvas.clientHeight) / h };
  }

  /** Floating damage / healing numbers. Rapid hits on one target merge into a running total. */
  private floatNumber(key: string, pos: Vec3, amount: number, kind: "dmg" | "crit" | "heal" | "ko", prefix = "") {
    if (amount <= 0) return;
    const now = performance.now();
    let f = this.floaters.get(key);
    if (!f || now - f.at > 400) {
      const el = document.createElement("div");
      this.tagsEl.appendChild(el);
      f = { el, amount: 0, at: now, timer: 0 };
      this.floaters.set(key, f);
      f.el.dataset.jx = String(Math.round((Math.random() - 0.5) * 40));
    }
    f.amount += amount;
    f.at = now;
    const crit = kind === "crit" || f.el.classList.contains("crit");
    f.el.className = `floater ${kind === "ko" ? "ko" : kind === "heal" ? "heal" : crit ? "crit" : "dmg"}`;
    f.el.textContent = kind === "ko" ? "KO!" : `${prefix}${Math.round(f.amount)}`;
    const sp = this.toScreen(pos);
    if (sp) {
      f.el.style.left = `${sp.x + Number(f.el.dataset.jx)}px`;
      f.el.style.top = `${sp.y}px`;
      f.el.style.display = "";
    } else f.el.style.display = "none";
    f.el.style.animation = "none";
    void f.el.offsetWidth;
    f.el.style.animation = "";
    clearTimeout(f.timer);
    const entry = f;
    f.timer = window.setTimeout(() => {
      entry.el.remove();
      if (this.floaters.get(key) === entry) this.floaters.delete(key);
    }, kind === "ko" ? 1300 : 950);
  }

  // ------------------------------------------------------------------ scene objects

  private buildObjective() {
    for (const o of this.objective) o.dispose();
    this.objective = [];
    this.packs = [];
    this.packBases = [];
    this.pointRing = this.pointDisc = undefined;
    this.cart = undefined;
    this.checkpointBands = [];
    this.gates = [];
    const scene = this.world.scene;
    if (this.map.track) this.buildConvoy();
    else this.placePoint();
    for (const hp of this.map.healthPacks) {
      const s = hp.large ? 0.55 : 0.35;
      const a = MeshBuilder.CreateBox("hpA", { width: s, height: s * 0.3, depth: s * 0.3 }, scene);
      const b = MeshBuilder.CreateBox("hpB", { width: s * 0.3, height: s * 0.3, depth: s }, scene);
      const cross = Mesh.MergeMeshes([a, b], true)!;
      cross.material = glow(scene, "#7dff8a");
      cross.position.set(hp.pos.x, hp.pos.y + 0.6, hp.pos.z);
      const base = MeshBuilder.CreateCylinder("hpBase", { diameter: hp.large ? 1.4 : 1, height: 0.1, tessellation: 24 }, scene);
      base.position.set(hp.pos.x, hp.pos.y + 0.05, hp.pos.z);
      base.material = toon(scene, "#3a4152");
      this.packs.push(cross);
      this.packBases.push(base);
      this.objective.push(cross, base);
    }
    this.stagePacks();
  }

  /** Control: the point's ring and disc, on the stage in play. */
  private placePoint() {
    const scene = this.world.scene;
    for (const m of [this.pointRing, this.pointDisc]) {
      if (!m) continue;
      m.dispose();
      const i = this.objective.indexOf(m);
      if (i >= 0) this.objective.splice(i, 1);
    }
    const pt = pointAt(this.map, this.stage);
    this.pointRing = MeshBuilder.CreateTorus("pointRing", { diameter: pt.radius * 2, thickness: 0.18, tessellation: 64 }, scene);
    this.pointRing.position.set(pt.pos.x, pt.pos.y + 0.08, pt.pos.z);
    this.pointRing.material = glow(scene, NEUTRAL);
    this.pointDisc = MeshBuilder.CreateDisc("pointDisc", { radius: pt.radius, tessellation: 64 }, scene);
    this.pointDisc.rotation.x = -Math.PI / 2;
    this.pointDisc.position.set(pt.pos.x, pt.pos.y + 0.03, pt.pos.z);
    this.pointDisc.material = glow(scene, NEUTRAL, 0.12);
    this.objective.push(this.pointRing, this.pointDisc);
  }

  /** Control: moves the world, the point and the packs on show to another stage. */
  private showStage(stage: number) {
    this.stage = stage;
    this.world.setStage(stage);
    if (!this.map.track) this.placePoint();
    this.stagePacks();
  }

  /** Only the stage in play's pack stands are drawn (the packs follow the snapshot's mask). */
  private stagePacks() {
    this.packBases.forEach((b, i) => {
      const here = this.map.healthPacks[i]?.stage;
      b.setEnabled(here === undefined || here === this.stage);
    });
  }

  /** Convoy: the route and payload (dressed by the map's look), checkpoint gates and spawn gates. */
  private buildConvoy() {
    const scene = this.world.scene;
    const track = this.map.track!;
    const own = <T extends { dispose(): void }>(m: T) => (this.objective.push(m), m);
    const look = this.world.look;
    const ctx = { scene, track, ink: this.world.ink, own, shadows: this.world.shadows };
    (look.track ?? sleeperTrack)(ctx);
    // Checkpoints and the end of the line: a light gate over the track.
    const marks = [...track.checkpoints.map((c) => c.dist), track.length];
    marks.forEach((d, k) => {
      const p = trackPose(track, d);
      const root = own(new TransformNode(`checkpoint${k}`, scene));
      root.position.set(p.x, p.y, p.z);
      root.rotation.y = Math.atan2(p.fx, p.fz);
      const final = k === marks.length - 1;
      const h = final ? 4.4 : 3.6;
      for (const x of [-2.7, 2.7]) {
        const post = MeshBuilder.CreateBox("cpPost", { width: 0.22, height: h, depth: 0.22 }, scene);
        post.parent = root;
        post.position.set(x, h / 2, 0);
        post.material = glow(scene, NEUTRAL, 0.55);
      }
      const band = MeshBuilder.CreateBox("cpBand", { width: 5.6, height: final ? 0.5 : 0.3, depth: 0.22 }, scene);
      band.parent = root;
      band.position.set(0, h, 0);
      band.material = glow(scene, GOLD);
      const stripe = MeshBuilder.CreateBox("cpStripe", { width: 2.8, height: 0.02, depth: 0.45 }, scene);
      stripe.parent = root;
      stripe.position.set(0, 0.03, 0);
      stripe.material = glow(scene, GOLD, 0.75);
      this.checkpointBands.push(band, stripe);
    });
    // The payload: the map dresses CART's two collision boxes.
    this.cart = (look.payload ?? flatbedPayload)(ctx);
    // Spawn gates: light walls across the attackers' doors through Setup.
    for (const g of this.map.gates) {
      const m = own(MeshBuilder.CreateBox("gate", { width: g.max.x - g.min.x, height: g.max.y - g.min.y, depth: Math.max(0.12, g.max.z - g.min.z) }, scene));
      m.position.set((g.min.x + g.max.x) / 2, (g.min.y + g.max.y) / 2, (g.min.z + g.max.z) / 2);
      m.material = glow(scene, GOLD, 0.35);
      m.isPickable = false;
      m.setEnabled(false);
      this.gates.push(m);
    }
  }

  private relTeam(t: number): "friend" | "foe" | "none" {
    return t === Team.None ? "none" : t === this.myTeam ? "friend" : "foe";
  }

  // ------------------------------------------------------------------ frame

  private frame() {
    if (this.disposed) return;
    const now = performance.now();
    const dt = Math.min(0.1, (now - this.lastFrame) / 1000);
    this.lastFrame = now;
    this.fpsFrames++;
    this.fpsTime += dt;
    if (this.fpsTime > 0.5) {
      ui.fps.value = Math.round(this.fpsFrames / this.fpsTime);
      this.fpsFrames = 0;
      this.fpsTime = 0;
    }
    ui.locked.value = this.input.locked;
    const net = this.net;
    const blocked = ui.overlay.value !== "none" || ui.chatOpen.value || !this.input.locked;
    const sample = this.input.sample(dt);
    this.lastButtons = sample.buttons;
    if (blocked && !this.input.gamepadConnected) {
      sample.buttons = 0;
      sample.moveX = 0;
      sample.moveZ = 0;
    }
    this.photo.apply(sample);

    const sim = net.predict;
    const eid = net.localEid;
    const before = eid !== undefined ? sim.posOf(eid) : undefined;
    const ticks = net.update(dt, sample);
    if (ticks > 0) this.input.ticked();
    if (eid !== undefined && net.localEid === eid) {
      const after = sim.posOf(eid);
      if (ticks > 0 && before) {
        this.renderPrev = ticks === 1 ? before : { x: after.x - (after.x - before.x) / ticks, y: after.y - (after.y - before.y) / ticks, z: after.z - (after.z - before.z) / ticks };
        this.renderCurr = after;
      } else if (!before) this.renderPrev = this.renderCurr = after;
    }
    for (const p of sim.predictedProjectiles) this.fx.addPredicted(p, net.localNetId);
    sim.predictedProjectiles.length = 0;

    const decay = Math.exp(-dt * 14);
    this.visualErr.x *= decay;
    this.visualErr.y *= decay;
    this.visualErr.z *= decay;
    const a = Math.min(1, net.alpha);
    const rp = {
      x: this.renderPrev.x + (this.renderCurr.x - this.renderPrev.x) * a + this.visualErr.x,
      y: this.renderPrev.y + (this.renderCurr.y - this.renderPrev.y) * a + this.visualErr.y,
      z: this.renderPrev.z + (this.renderCurr.z - this.renderPrev.z) * a + this.visualErr.z,
    };

    const ents = net.interpolate();
    const latest = net.latest();
    const meRec = latest?.get(net.localNetId);
    if (meRec) this.myTeam = meRec.team as Team;
    const alive = meRec ? !!meRec.alive : false;

    this.updateCamera(rp, sample.yaw, sample.pitch, alive, dt, sample.buttons);
    this.buildLookup(ents, rp);
    this.updateLocalAvatar(rp, sample.yaw, sample.pitch, alive, dt);
    this.updateRemotes(ents, dt);
    this.updateHitboxes(ents);
    this.updateWorldObjects(ents, latest);
    this.fx.update(dt, (o, d, m) => sim.physics.raycast(o, d, m));
    this.feedAudio(dt, ents, latest, rp);

    this.hudTimer += dt;
    if (this.hudTimer > 1 / 20) {
      this.hudTimer = 0;
      this.publishHud(latest);
    }
    this.world.scene.render();
  }

  private updateCamera(rp: Vec3, yaw: number, pitch: number, alive: boolean, dt: number, buttons: number) {
    const eid = this.net.localEid;
    const hero = eid !== undefined ? this.net.predict.hero(eid) : HERO_LIST[0]!;
    const crouch = eid !== undefined && !!this.net.predict.c.Player.crouch[eid];
    const eye = { x: rp.x, y: rp.y + eyeHeight(hero, crouch), z: rp.z };
    const cs = cameraScale(hero.height);
    let cam = cameraPos(eye, yaw, pitch, cs);
    if (!alive) cam = cameraPos({ ...eye, y: eye.y + 2.5 }, yaw, Math.max(pitch, -0.2), cs, 6);
    // Visual camera collision (aim still uses the unobstructed camera ray, like the server).
    const d = { x: cam.x - eye.x, y: cam.y - eye.y, z: cam.z - eye.z };
    const len = Math.hypot(d.x, d.y, d.z);
    const hit = this.net.predict.physics.raycast(eye, { x: d.x / len, y: d.y / len, z: d.z / len }, len);
    if (hit >= 0) {
      const k = Math.max(0.3, hit - 0.25) / len;
      cam = { x: eye.x + d.x * k, y: eye.y + d.y * k, z: eye.z + d.z * k };
    }
    const scope = eid !== undefined ? CLIENT_HEROES[hero.id].scope?.(this.net.predict.c, eid, buttons, alive) : undefined;
    const fovTarget = scope ?? 1.15;
    this.camera.fov += (fovTarget - this.camera.fov) * Math.min(1, dt * 16);
    this.input.sensMul = scope !== undefined ? 0.45 : 1;
    ui.scoped.value = scope !== undefined;
    this.shake *= Math.exp(-dt * 10);
    const s = this.shake;
    const pos = new Vector3(cam.x + (Math.random() - 0.5) * s, cam.y + (Math.random() - 0.5) * s, cam.z + (Math.random() - 0.5) * s);
    this.camera.position.copyFrom(pos);
    const f = viewDir(yaw, pitch);
    this.camera.setTarget(pos.add(new Vector3(f.x, f.y, f.z)));
    // Hear from the hero's head (so a flanker behind you is behind you), looking where the camera looks.
    audio.setListener(alive ? eye : cam, f);
    this.localCameraVis = hit >= 0 && hit < 1.2 ? 0.25 : 1;
    const shot = this.photo.frame(rp, yaw, hero.height);
    if (shot) {
      this.camera.position.set(shot.pos.x, shot.pos.y, shot.pos.z);
      this.camera.setTarget(new Vector3(shot.look.x, shot.look.y, shot.look.z));
      this.localCameraVis = 1;
    }
    const fly = this.photo.fly;
    this.camera.mode = fly?.ortho ? Camera.ORTHOGRAPHIC_CAMERA : Camera.PERSPECTIVE_CAMERA;
    if (fly) {
      this.camera.position.set(fly.pos.x, fly.pos.y, fly.pos.z);
      this.camera.setTarget(new Vector3(fly.look.x, fly.look.y, fly.look.z));
      if (fly.fov) this.camera.fov = fly.fov;
      if (fly.ortho) {
        const half = fly.ortho / 2;
        const aspect = this.world.engine.getAspectRatio(this.camera);
        this.camera.orthoTop = half;
        this.camera.orthoBottom = -half;
        this.camera.orthoLeft = -half * aspect;
        this.camera.orthoRight = half * aspect;
      }
      this.localCameraVis = 1;
    }
  }

  private avatarStatus(av: Avatar) {
    let fx = this.statusFx.get(av);
    if (!fx) {
      const def = heroDef(av.hero, av.form ?? 0);
      fx = new StatusFx(this.world.scene, av.root, def.height, def.radius);
      this.statusFx.set(av, fx);
    }
    return fx;
  }

  private presentAvatar(av: Avatar, netId: number, status: number, rel: "self" | "friend" | "foe", pos: Vec3, speed: number, dt: number, baseVis = 1) {
    const stealthed = (status & STATUS_BITS.stealth) !== 0;
    const d = Math.hypot(pos.x - this.camera.position.x, pos.y - this.camera.position.y, pos.z - this.camera.position.z);
    const reveal = rel === "foe" && stealthed && (d <= 4 || (this.stealthRevealUntil.get(netId) ?? 0) > performance.now());
    const mode = rel === "foe" && stealthed ? (reveal ? "stealth-shimmer" : "hidden") : "normal";
    const bodyVis = rel !== "foe" && stealthed ? Math.min(baseVis, 0.35) : mode === "normal" ? baseVis : 0;
    for (const m of av.meshes) {
      m.visibility = bodyVis;
      if (rel !== "self") m.renderOutline = bodyVis > 0.01 && !(stealthed && rel === "friend");
    }
    for (const m of av.worldMeshes) m.visibility = bodyVis;
    this.avatarStatus(av).update(dt, status, speed, mode, baseVis);
  }

  private lookupRefs = new Map<number, WorldRef>();
  /** Players and constructs as rendered this frame, by net id (AvatarFrame.lookup). */
  private readonly lookup = (netId: number) => this.lookupRefs.get(netId);
  private buildLookup(ents: ReturnType<NetClient["interpolate"]>, rp: Vec3) {
    const refs = this.lookupRefs;
    refs.clear();
    for (const [id, e] of ents) {
      const f = e.f;
      if (e.type === EntityType.Player) {
        const h = heroDef(f.hero as HeroId, f.form ?? 0)?.height ?? 1.8;
        refs.set(id, { pos: { x: f.x!, y: f.y!, z: f.z! }, center: { x: f.x!, y: f.y! + h / 2, z: f.z! }, height: h, alive: !!f.alive, team: f.team!, player: true });
      } else if (e.type === EntityType.Construct) {
        const h = CONSTRUCTS[f.kind!]?.shape.h ?? 1;
        refs.set(id, { pos: { x: f.x!, y: f.y!, z: f.z! }, center: { x: f.x!, y: f.y! + h / 2, z: f.z! }, height: h, alive: true, team: f.team!, player: false });
      }
    }
    const eid = this.net.localEid;
    if (eid !== undefined) {
      const c = this.net.predict.c;
      const h = this.net.predict.hero(eid).height;
      refs.set(this.net.localNetId, { pos: rp, center: { x: rp.x, y: rp.y + h / 2, z: rp.z }, height: h, alive: !!c.Player.alive[eid], team: c.Player.team[eid]!, player: true });
    }
  }

  private updateLocalAvatar(rp: Vec3, yaw: number, pitch: number, alive: boolean, dt: number) {
    const net = this.net;
    const eid = net.localEid;
    if (eid === undefined) return;
    const sim = net.predict;
    const hero = sim.c.Player.hero[eid] as HeroId;
    const form = sim.c.Player.form[eid]!;
    if (hero !== this.localHero || form !== this.localForm) {
      if (this.local) {
        this.statusFx.get(this.local)?.dispose();
        this.local.dispose();
      }
      this.local = buildAvatar(this.world.scene, hero, null, form);
      this.local.root.scaling.set(1.08, 1.08, 1.08);
      for (const m of this.local.meshes) this.world.shadows.addShadowCaster(m);
      this.localHero = hero;
      this.localForm = form;
    }
    const av = this.local!;
    av.root.setEnabled(alive);
    av.root.position.set(rp.x, rp.y, rp.z);
    av.root.rotation.y = yaw;
    const c = sim.c;
    const sp = Math.hypot(c.Vel.x[eid]!, c.Vel.z[eid]!);
    // v0..v3 exactly as others receive them (u16 on the wire), so a hero's animate() looks the same for everyone.
    for (let i = 0; i < 4; i++) this.localVars[i] = Math.max(0, Math.min(65535, Math.round(c.HeroVars[eid * HERO_VARS + i]!)));
    const status = statusBits(sim, eid);
    animateAvatar(av, {
      speed: sp,
      pitch,
      crouch: !!c.Player.crouch[eid],
      grounded: !!c.Player.grounded[eid],
      dt,
      action: c.Weapon.action[eid]!,
      status,
      v: this.localVars,
      form,
      disguised: sim.isDisguised(eid),
      ultActive: c.Ult.active[eid]! > 0,
      moveKind: c.Move.ticks[eid]! > 0 ? c.Move.kind[eid]! : 0,
      self: true,
      friend: true,
      netId: net.localNetId,
      pos: rp,
      yaw,
      eye: { x: rp.x, y: rp.y + eyeHeight(sim.hero(eid), !!c.Player.crouch[eid]), z: rp.z },
      aim: viewDir(yaw, pitch),
      lookup: this.lookup,
    });
    this.presentAvatar(av, net.localNetId, status, "self", rp, sp, dt, this.localCameraVis);
  }

  private updateRemotes(ents: ReturnType<NetClient["interpolate"]>, dt: number) {
    const scene = this.world.scene;
    const me = this.net.localNetId;
    const seen = new Set<number>();
    const w = this.world.engine.getRenderWidth();
    const h = this.world.engine.getRenderHeight();
    const viewProj = scene.getTransformMatrix();
    const vp = this.camera.viewport.toGlobal(w, h);
    const camPos = this.camera.position;
    const scaleX = this.canvas.clientWidth / w;
    const scaleY = this.canvas.clientHeight / h;
    for (const [id, e] of ents) {
      if (e.type !== EntityType.Player || id === me) continue;
      const f = e.f;
      seen.add(id);
      let r = this.remotes.get(id);
      const friend = f.team === this.myTeam;
      const form = f.form ?? 0;
      if (!r || r.avatar.hero !== f.hero || r.form !== form) {
        if (r) {
          this.statusFx.get(r.avatar)?.dispose();
          this.statusFx.delete(r.avatar);
          r.avatar.dispose();
          r.tag.remove();
        }
        const avatar = buildAvatar(scene, f.hero as HeroId, friend ? FRIEND : FOE, form);
        avatar.root.scaling.set(1.08, 1.08, 1.08);
        for (const m of avatar.meshes) this.world.shadows.addShadowCaster(m);
        const tag = document.createElement("div");
        tag.className = `nametag ${friend ? "friend" : "foe"}`;
        tag.innerHTML = `<span class="nt-name"></span><span class="nt-bar"><i></i></span>`;
        this.tagsEl.appendChild(tag);
        r = { avatar, form, last: { x: f.x!, y: f.y!, z: f.z! }, speed: 0, tag, vars: [0, 0, 0, 0] };
        this.remotes.set(id, r);
      }
      const av = r.avatar;
      av.root.setEnabled(!!f.alive);
      av.root.position.set(f.x!, f.y!, f.z!);
      av.root.rotation.y = f.yaw!;
      const inst = Math.hypot(f.x! - r.last.x, f.z! - r.last.z) / Math.max(dt, 1e-3);
      r.speed += (Math.min(inst, 20) - r.speed) * Math.min(1, dt * 10);
      r.last = { x: f.x!, y: f.y!, z: f.z! };
      r.vars[0] = f.v0 ?? 0;
      r.vars[1] = f.v1 ?? 0;
      r.vars[2] = f.v2 ?? 0;
      r.vars[3] = f.v3 ?? 0;
      animateAvatar(av, {
        speed: r.speed,
        pitch: f.pitch!,
        crouch: !!f.crouch,
        grounded: !!f.grounded,
        dt,
        action: f.action!,
        status: f.status ?? 0,
        v: r.vars,
        form,
        disguised: !!f.disguised,
        ultActive: !!f.ultActive,
        moveKind: f.moveKind ?? 0,
        self: false,
        friend,
        netId: id,
        pos: { x: f.x!, y: f.y!, z: f.z! },
        yaw: f.yaw!,
        eye: { x: f.x!, y: f.y! + eyeHeight(heroDef(f.hero as HeroId, form), !!f.crouch), z: f.z! },
        aim: viewDir(f.yaw!, f.pitch!),
        lookup: this.lookup,
      });
      const rel = friend ? "friend" : "foe";
      this.presentAvatar(av, id, f.status ?? 0, rel, { x: f.x!, y: f.y!, z: f.z! }, r.speed, dt);
      // Name tags / health bars
      const def = heroDef(f.hero as HeroId, form);
      const head = new Vector3(f.x!, f.y! + def.height + 0.45, f.z!);
      const sp = Vector3.Project(head, Matrix.IdentityReadOnly, viewProj, vp);
      const dist = Vector3.Distance(head, camPos);
      const maxHp = def.health + def.armor + def.shields;
      const hpNow = f.hp! + f.armor! + f.shield!;
      const onScreen = f.alive && sp.z > 0 && sp.z < 1;
      const visible = onScreen && (friend || (this.net.predict.physics.los({ x: camPos.x, y: camPos.y, z: camPos.z }, { x: head.x, y: head.y - 0.5, z: head.z }) && dist < 60));
      const showFoe = visible && !friend && hpNow < maxHp - 0.5;
      const hiddenByStealth = !friend && !!((f.status ?? 0) & STATUS_BITS.stealth) && Math.hypot(f.x! - this.camera.position.x, f.y! - this.camera.position.y, f.z! - this.camera.position.z) > 4 && (this.stealthRevealUntil.get(id) ?? 0) <= performance.now();
      if (!hiddenByStealth && visible && (friend || showFoe)) {
        r.tag.style.display = "";
        r.tag.style.transform = `translate(${sp.x * scaleX}px, ${sp.y * scaleY}px) translate(-50%, -100%)`;
        const nameEl = r.tag.firstElementChild as HTMLElement;
        const name = friend ? this.nameOf(id) : "";
        if (nameEl.textContent !== name) nameEl.textContent = name;
        ((r.tag.lastElementChild as HTMLElement).firstElementChild as HTMLElement).style.width = `${Math.max(0, Math.min(100, (hpNow / maxHp) * 100))}%`;
        r.tag.style.opacity = String(Math.max(0.35, Math.min(1, 1.4 - dist / 50)));
      } else r.tag.style.display = "none";
    }
    for (const [id, r] of this.remotes) {
      if (seen.has(id)) continue;
      this.statusFx.get(r.avatar)?.dispose();
      r.avatar.dispose();
      r.tag.remove();
      this.remotes.delete(id);
    }
  }

  /** F4: draw remote hitboxes where the client sees them — exactly what lag compensation rewinds to. */
  private updateHitboxes(ents: ReturnType<NetClient["interpolate"]>) {
    const show = ui.hitboxes.value;
    const seen = new Set<number>();
    if (show) {
      for (const [id, e] of ents) {
        if (e.type !== EntityType.Player || id === this.net.localNetId || !e.f.alive) continue;
        seen.add(id);
        const caps = heroCapsules(heroDef(e.f.hero as HeroId, e.f.form ?? 0), !!e.f.crouch);
        let meshes = this.hitboxMeshes.get(id);
        if (!meshes || meshes.length !== caps.length) {
          meshes?.forEach((m) => m.dispose());
          meshes = caps.map((c) => {
            const m = MeshBuilder.CreateCapsule("hitbox", { radius: c.r, height: Math.max(c.r * 2, c.b - c.a + c.r * 2), tessellation: 10 }, this.world.scene);
            m.material = glow(this.world.scene, c.head ? "#ff5b45" : "#e8b84a", 0.35);
            m.isPickable = false;
            return m;
          });
          this.hitboxMeshes.set(id, meshes);
        }
        caps.forEach((c, i) => meshes![i]!.position.set(e.f.x!, e.f.y! + (c.a + c.b) / 2, e.f.z!));
      }
    }
    for (const [id, ms] of this.hitboxMeshes) {
      if (seen.has(id)) continue;
      ms.forEach((m) => m.dispose());
      this.hitboxMeshes.delete(id);
    }
  }

  private updateWorldObjects(ents: ReturnType<NetClient["interpolate"]>, latest: Map<number, Record<string, number>> | undefined) {
    const me = this.net.localNetId;
    const sim = this.net.predict;
    const leid = this.net.localEid;
    const projectiles: ProjectileSync[] = [];
    const zones: { netId: number; kind: number; pos: Vec3; radius: number; ticks: number; rel: ViewRel }[] = [];
    const constructs: ConstructRenderFrame[] = [];
    const barriers: { netId: number; pos: Vec3; yaw: number; hpFrac: number; color: string; spec: NonNullable<(typeof BARRIERS)[HeroId]> }[] = [];
    const beams: { id: number; from: Vec3; to: Vec3; color: string }[] = [];
    const predictedSeqs = this.fx.predictedSeqs();
    const hidden = new Set<number>();
    const playerPos = new Map<number, Vec3>();
    for (const [id, e] of ents) if (e.type === EntityType.Player) playerPos.set(id, { x: e.f.x!, y: e.f.y!, z: e.f.z! });
    if (leid !== undefined) playerPos.set(me, sim.posOf(leid));

    for (const [id, e] of ents) {
      const f = e.f;
      if (e.type === EntityType.Projectile) {
        if (f.owner === me && predictedSeqs.has(f.seq!)) hidden.add(id);
        const owner = f.owner ?? 0;
        const ot = owner === me ? undefined : latest?.get(owner)?.team;
        const rel: ViewRel = owner === me ? "self" : ot === undefined ? "none" : ot === this.myTeam ? "friend" : "foe";
        projectiles.push({ netId: id, kind: f.kind!, owner, pos: { x: f.x!, y: f.y!, z: f.z! }, vel: { x: f.vx ?? 0, y: f.vy ?? 0, z: f.vz ?? 0 }, rel });
      } else if (e.type === EntityType.Zone) {
        const rel: ViewRel = f.team === Team.None || f.team === undefined ? "none" : f.team === this.myTeam ? "friend" : "foe";
        zones.push({ netId: id, kind: f.kind!, pos: { x: f.x!, y: f.y!, z: f.z! }, radius: f.radius!, ticks: f.ticks!, rel });
      } else if (e.type === EntityType.Construct) {
        const spec = CONSTRUCTS[f.kind!];
        if (!spec) continue;
        const owner = f.owner ?? 0;
        const ownerRec = latest?.get(owner);
        const rel = owner === me ? "self" : f.team === Team.None ? "none" : f.team === this.myTeam ? "friend" : "foe";
        const hero = ownerRec?.hero as HeroId | undefined;
        const frame: ConstructFrame = {
          netId: id,
          kind: f.kind!,
          owner,
          pos: { x: f.x!, y: f.y!, z: f.z! },
          yaw: f.yaw ?? 0,
          hpFrac: Math.max(0, Math.min(1, (f.hp ?? spec.maxHp) / spec.maxHp)),
          materialize: constructMaterialize(spec, f.age ?? 0),
          ticks: f.ticks ?? 0,
          v0: f.v0 ?? 0,
          flags: f.flags ?? 0,
          team: f.team ?? Team.None,
          rel,
          spec,
        };
        constructs.push({ ...frame, build: hero !== undefined ? CLIENT_HEROES[hero].constructs?.[f.kind!] : undefined });
      } else if (e.type === EntityType.Player && f.alive) {
        const isMe = id === me && leid !== undefined;
        const color = f.team === this.myTeam ? FRIEND : FOE;
        const spec = BARRIERS[f.hero as HeroId];
        if (spec) {
          const vars = [f.v0 ?? 0, f.v1 ?? 0, f.v2 ?? 0, f.v3 ?? 0];
          const up = isMe ? hv(sim.c, leid!, spec.upVar) : vars[spec.upVar];
          const hp = isMe ? hv(sim.c, leid!, spec.hpVar) : vars[spec.hpVar];
          if (up) barriers.push({ netId: id, pos: isMe ? this.renderCurr : { x: f.x!, y: f.y!, z: f.z! }, yaw: isMe ? this.input.yaw : f.yaw!, hpFrac: hp! / spec.maxHp, color, spec });
        }
      }
    }
    this.fx.syncProjectiles(projectiles, (id) => hidden.has(id));
    this.fx.syncZones(zones);
    this.fx.syncConstructs(constructs);
    this.fx.syncBarriers(barriers);
    // The aimed ability's preview, at the look the camera has right now (between ticks).
    const aiming = leid !== undefined ? targetingSlot(sim, leid) : undefined;
    const aimSpec = aiming !== undefined ? targetSpec(sim, leid!, aiming) : undefined;
    if (aimSpec && aiming !== undefined && sim.c.Player.alive[leid!]) {
      const t = previewTargetPoint(sim, leid!, aiming, this.input.yaw, this.input.pitch);
      this.targetPreview.show(aimSpec, t.pos, t.yaw, FRIEND);
    } else this.targetPreview.hide();
    this.fx.syncBeams(beams);

    const m = latest?.get(MATCH_NET_ID);
    if (!m) return;
    const scene = this.world.scene;
    const stage = m.mode === GameMode.Control ? (m.stage ?? 0) : 0;
    if (stage !== this.stage || stage !== this.world.stage) this.showStage(stage);
    if (this.pointRing && this.pointDisc) {
      const owner = this.relTeam(m.owner!);
      const color = owner === "friend" ? FRIEND : owner === "foe" ? FOE : NEUTRAL;
      this.pointRing.material = glow(scene, m.contested ? GOLD : color);
      this.pointDisc.material = glow(scene, color, 0.12);
    }
    const track = this.map.track;
    if (this.cart && track && m.mode === GameMode.Convoy) {
      // Interpolated, so the payload glides like everything else.
      const cartMm = ents.get(MATCH_NET_ID)?.f.cart ?? m.cart!;
      const pose = trackPose(track, cartMm / 1000);
      const { root, glows, ring, orb } = this.cart;
      root.position.set(pose.x, pose.y, pose.z);
      root.rotation.y = Math.atan2(pose.fx, pose.fz);
      // Pitched to the slope under it (its collision boxes stay level).
      const back = trackPose(track, cartMm / 1000 - 0.9);
      const ahead = trackPose(track, cartMm / 1000 + 0.9);
      root.rotation.x = -Math.atan2(ahead.y - back.y, Math.hypot(ahead.x - back.x, ahead.z - back.z) || 1);
      const atk = this.relColor(m.attacker!);
      const def = this.relColor(otherTeam(m.attacker as Team));
      const st = m.cartState as CartState;
      const color = st === CartState.Pushing ? atk : st === CartState.Contested ? GOLD : st === CartState.Blocked || st === CartState.Rolling ? def : NEUTRAL;
      for (const g of glows) g.material = glow(scene, color);
      ring.material = glow(scene, color, st === CartState.Idle ? 0.35 : 0.7);
      const t = performance.now() / 1000;
      orb.position.y = this.cart.orbHeight + Math.sin(t * 2.2) * 0.06;
      orb.rotation.y = t;
      this.cart.update?.(t, st === CartState.Pushing || st === CartState.Rolling);
      // Checkpoint gates take the attackers' colour once passed.
      const done = [...track.checkpoints.map((_, k) => k < m.checkpoint!), cartMm >= track.length * 1000 - 1];
      this.checkpointBands.forEach((b, i) => (b.material = glow(scene, done[i >> 1] ? atk : GOLD, i % 2 ? 0.75 : 1)));
      const closed = m.phase === Phase.Setup;
      for (const g of this.gates) {
        g.setEnabled(closed);
        if (closed) g.material = glow(scene, atk, 0.35 + Math.sin(t * 5) * 0.08);
      }
    }
    const packs = m.packs!;
    this.packs.forEach((p, i) => {
      const here = this.map.healthPacks[i]?.stage;
      p.setEnabled(!!(packs & (1 << i)) && (here === undefined || here === this.stage));
      p.rotation.y += 0.03;
    });
  }

  private relColor(t: number) {
    const r = this.relTeam(t);
    return r === "friend" ? FRIEND : r === "foe" ? FOE : NEUTRAL;
  }

  // ------------------------------------------------------------------ HUD

  private publishHud(latest: Map<number, Record<string, number>> | undefined) {
    const net = this.net;
    const eid = net.localEid;
    ui.net.value = { ...net.stats };
    if (eid === undefined || !latest) return;
    const sim = net.predict;
    const c = sim.c;
    const me = latest.get(net.localNetId);
    if (!me) return;
    const hero = me.hero as HeroId;
    const form = me.form ?? c.Player.form[eid] ?? 0;
    const def = heroDef(hero, form);
    const abilities: AbilityHud[] = [Slot.Secondary, Slot.Ability1, Slot.Ability2, Slot.Ability3].flatMap((slot) => {
      const info = def.abilities[slot];
      if (!info) return [];
      const i = abilIdx(eid, slot);
      const cd = c.Abil.cd[i]!;
      return [{
        slot,
        key: info.key,
        name: info.name,
        cdSec: cd / TICK_RATE,
        cdFrac: info.cooldown > 0 ? cd / (info.cooldown * TICK_RATE) : 0,
        charges: c.Abil.charges[i]!,
        maxCharges: info.charges ?? 1,
        hasCooldown: info.cooldown > 0,
      }];
    });
    const status: string[] = [];
    const st = me.status!;
    if (st & STATUS_BITS.stun) status.push("Stunned");
    if (st & STATUS_BITS.sleep) status.push("Asleep");
    if (st & STATUS_BITS.knockdown) status.push("Knocked down");
    if (st & STATUS_BITS.root) status.push("Rooted");
    if (st & STATUS_BITS.slow) status.push("Slowed");
    if (st & STATUS_BITS.pinned) status.push("Pinned");
    if (st & STATUS_BITS.boost) status.push("Damage boost");
    if (st & STATUS_BITS.invuln) status.push("Invulnerable");
    if (st & STATUS_BITS.antiHeal) status.push("Healing blocked");
    if (st & STATUS_BITS.healBoost) status.push("Healing boosted");
    if (st & STATUS_BITS.resist) status.push("Damage reduced");
    if (st & STATUS_BITS.linked) status.push("Soul-linked");
    if (st & STATUS_BITS.stealth) status.push("Stealthed");
    if (st & STATUS_BITS.haste) status.push("Hasted");
    if (st & STATUS_BITS.unstoppable) status.push("Unstoppable");
    if (st & STATUS_BITS.marked) status.push("Marked");
    if (st & STATUS_BITS.bonded) status.push("Bonded");
    if (st & STATUS_BITS.frozen) status.push("Frozen");
    if (st & STATUS_BITS.held) status.push("Held");
    const resources = CLIENT_HEROES[hero].hud?.(c, eid) ?? [];

    const mr = latest.get(MATCH_NET_ID);
    let match: MatchHud | undefined;
    if (mr) {
      const fi = this.myTeam === Team.B ? 1 : 0;
      const score: [number, number] = [mr.scoreA!, mr.scoreB!];
      const prog: [number, number] = [mr.progA!, mr.progB!];
      const on: [number, number] = [mr.onA!, mr.onB!];
      const track = this.map.track;
      let convoy: ConvoyHud | undefined;
      if (mr.mode === GameMode.Convoy && track) {
        const first = otherTeam(mr.attacker as Team);
        convoy = {
          attacking: mr.attacker === this.myTeam,
          cart: Math.min(1, mr.cart! / 1000 / track.length),
          checkpoints: track.checkpoints.map((c) => c.dist / track.length),
          passed: mr.checkpoint!,
          clockSec: Math.ceil(mr.clock! / TICK_RATE),
          state: mr.cartState as CartState,
          pushers: [on[fi]!, on[1 - fi]!],
          goal:
            mr.round! >= 2
              ? { frac: prog[first === Team.A ? 0 : 1]! / 100, delivered: !!mr.goalDone, leftSec: Math.ceil(mr.goalTicks! / TICK_RATE), ours: first === this.myTeam }
              : undefined,
        };
      }
      match = {
        mode: mr.mode as GameMode,
        convoy,
        phase: mr.phase as Phase,
        phaseSec: Math.ceil(mr.phaseTicks! / TICK_RATE),
        round: mr.round!,
        score: [score[fi]!, score[1 - fi]!],
        progress: [prog[fi]!, prog[1 - fi]!],
        owner: this.relTeam(mr.owner!),
        capture: mr.capture!,
        capTeam: this.relTeam(mr.capTeam!),
        contested: !!mr.contested,
        overtime: !!mr.overtime,
        onPoint: [on[fi]!, on[1 - fi]!],
        winner: this.relTeam(mr.winner!),
        stageName: mr.mode === GameMode.Control ? MAPS[this.mapId].stages?.[mr.stage ?? 0] : undefined,
      };
    }
    const ultCharge = c.Ult.charge[eid]!;
    if (me.alive) this.killedBy = undefined;
    const weapon = heldWeapon(def, c.Weapon.slot[eid]!);
    const aimSlot = targetingSlot(sim, eid);
    const aimSpec = aimSlot !== undefined ? targetSpec(sim, eid, aimSlot) : undefined;
    const targeting =
      aimSlot !== undefined && aimSpec
        ? { name: def.abilities[aimSlot]?.name ?? "", verb: aimSpec.verb ?? (aimSpec.start === "kit" ? "Confirm" : "Place"), cancel: aimSpec.start !== "kit" }
        : undefined;
    ui.hud.value = {
      hero,
      form,
      baseHero: (me.baseHero ?? hero) as HeroId,
      disguised: !!me.disguised,
      alive: !!me.alive,
      respawn: me.respawn!,
      hp: me.hp!,
      maxHp: def.health,
      armor: me.armor!,
      maxArmor: def.armor,
      shield: me.shield!,
      maxShield: def.shields,
      over: me.over!,
      ammo: Math.round(c.Weapon.ammo[eid]!),
      maxAmmo: weapon.ammo,
      reload: c.Weapon.reload[eid]! > 0 ? 1 - c.Weapon.reload[eid]! / (weapon.reloadTime * TICK_RATE) : 0,
      abilities,
      ult: ultCharge,
      ultActive: c.Ult.active[eid]! > 0,
      status,
      resources,
      match,
      inSpawn: sim.inSpawnRoom(eid),
      targeting,
      killedBy: this.killedBy,
      buttons: this.lastButtons,
      view: { x: this.renderCurr.x, z: this.renderCurr.z, yaw: this.input.yaw },
      roster: (ui.lobby.value?.players ?? []).map((p) => {
        const r = latest.get(p.netId);
        const friend = p.team === this.myTeam;
        return {
          id: p.id,
          name: p.name,
          hero: (r?.baseHero ?? p.hero) as HeroId,
          visibleHero: (r?.hero ?? p.hero) as HeroId,
          form: r?.form ?? 0,
          disguised: !!r?.disguised,
          friend,
          alive: r ? !!r.alive : true,
          respawn: r?.respawn ?? 0,
          ult: friend ? (r?.ult ?? 0) : 0,
          me: p.netId === net.localNetId,
        };
      }),
    };
  }

  // ------------------------------------------------------------------ audio

  /** Hand the audio director this frame's view of every player, projectile, zone and the match. */
  private feedAudio(dt: number, ents: ReturnType<NetClient["interpolate"]>, latest: Map<number, Record<string, number>> | undefined, rp: Vec3) {
    const director = this.director;
    if (!director) return;
    const net = this.net;
    const c = net.predict.c;
    const eid = net.localEid;
    const meId = net.localNetId;
    const meRec = latest?.get(meId);
    let self: SelfAudio | undefined;
    if (eid !== undefined && meRec) {
      const hero = c.Player.hero[eid] as HeroId;
      const form = c.Player.form[eid]!;
      const def = net.predict.hero(eid);
      const grounded = !!c.Player.grounded[eid];
      const v = [hv(c, eid, 0), hv(c, eid, 1), hv(c, eid, 2), hv(c, eid, 3)];
      self = {
        id: meId,
        rel: "self",
        hero,
        form,
        alive: !!meRec.alive,
        pos: rp,
        grounded,
        crouch: !!c.Player.crouch[eid],
        action: c.Weapon.action[eid]!,
        shots: c.Weapon.shots[eid]!,
        ultActive: c.Ult.active[eid]! > 0,
        status: meRec.status ?? 0,
        v,
        vy: c.Vel.y[eid]!,
        hp: meRec.hp ?? 0,
        armor: meRec.armor ?? 0,
        shield: meRec.shield ?? 0,
        maxHp: def.health + def.armor + def.shields,
        ult: c.Ult.charge[eid]!,
        ammo: c.Weapon.ammo[eid]!,
        lifting: CLIENT_HEROES[hero].lifting?.(c, eid, this.lastButtons, grounded) ?? false,
      };
    }
    const others: ActorAudio[] = [];
    const projectiles: ProjectileAudio[] = [];
    const zones: ZoneAudio[] = [];
    const constructs: ConstructAudio[] = [];
    for (const [id, e] of ents) {
      const f = e.f;
      const pos = { x: f.x!, y: f.y!, z: f.z! };
      if (e.type === EntityType.Player) {
        if (id === meId) continue;
        others.push({
          id,
          rel: f.team === this.myTeam ? "friend" : "foe",
          hero: f.hero as HeroId,
          form: f.form ?? 0,
          alive: !!f.alive,
          pos,
          grounded: !!f.grounded,
          crouch: !!f.crouch,
          action: f.action ?? 0,
          shots: f.shots ?? 0,
          ultActive: !!f.ultActive,
          status: f.status ?? 0,
          v: [f.v0 ?? 0, f.v1 ?? 0, f.v2 ?? 0, f.v3 ?? 0],
        });
      } else if (e.type === EntityType.Projectile) projectiles.push({ id, kind: f.kind!, owner: f.owner ?? 0, pos, vel: { x: f.vx ?? 0, y: f.vy ?? 0, z: f.vz ?? 0 } });
      else if (e.type === EntityType.Zone) zones.push({ id, kind: f.kind!, pos });
      else if (e.type === EntityType.Construct) {
        const owner = f.owner ?? 0;
        const ownerHero = latest?.get(owner)?.hero as HeroId | undefined;
        if (ownerHero !== undefined) constructs.push({ id, kind: f.kind!, owner, ownerHero, rel: this.relOfNet(owner), pos });
      }
    }
    const mr = latest?.get(MATCH_NET_ID);
    let match: MatchAudio | undefined;
    if (mr) {
      const fi = this.myTeam === Team.B ? 1 : 0;
      const score = [mr.scoreA!, mr.scoreB!];
      const prog = [mr.progA!, mr.progB!];
      match = {
        phase: mr.phase as Phase,
        phaseSec: Math.ceil(mr.phaseTicks! / TICK_RATE),
        round: mr.round!,
        score: [score[fi]!, score[1 - fi]!],
        progress: [prog[fi]!, prog[1 - fi]!],
        owner: this.relTeam(mr.owner!),
        capture: mr.capture!,
        capTeam: this.relTeam(mr.capTeam!),
        contested: !!mr.contested,
        overtime: !!mr.overtime,
        winner: this.relTeam(mr.winner!),
        roundsToWin: ui.lobby.value?.settings.match.roundsToWin ?? 2,
        point: pointAt(this.map, this.stage).pos,
        mode: mr.mode as GameMode,
      };
      const track = this.map.track;
      if (mr.mode === GameMode.Convoy && track) {
        const cartMm = ents.get(MATCH_NET_ID)?.f.cart ?? mr.cart!;
        const pose = trackPose(track, cartMm / 1000);
        const state = mr.cartState as CartState;
        const pushers = mr.attacker === Team.A ? mr.onA! : mr.onB!;
        match.convoy = {
          attacking: mr.attacker === this.myTeam,
          cart: { x: pose.x, y: pose.y, z: pose.z },
          state,
          speed: state === CartState.Pushing ? Math.min(3, pushers) / 3 : state === CartState.Rolling ? 0.3 : 0,
          checkpoint: mr.checkpoint!,
          frac: Math.min(1, mr.cart! / 1000 / track.length),
        };
      }
    }
    director.frame(dt, self, others, match, projectiles, zones, constructs);
  }

  send(msg: Parameters<NetClient["send"]>[0]) {
    this.net?.send(msg);
  }

  dispose() {
    this.disposed = true;
    this.director?.dispose();
    this.input.dispose();
    this.net?.close();
    for (const r of this.remotes.values()) r.tag.remove();
    this.fx?.dispose();
    this.targetPreview?.dispose();
    this.world?.dispose();
  }
}
